
import React, { ReactNode } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet";

interface CompanyLayoutProps {
  children: ReactNode;
  title: string;
  description: string;
  ogImage?: string;
  canonicalUrl?: string;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const CompanyLayout = ({ 
  children, 
  title, 
  description, 
  ogImage = "/assembly-vs-bonusly-hero.png",
  canonicalUrl,
  keywords = "employee recognition software, Assembly vs Bonusly, employee engagement platform, recognition platform comparison, G2 reviews",
  author = "Assembly Team",
  publishedTime,
  modifiedTime
}: CompanyLayoutProps) => {
  const fullTitle = `${title} | Assembly`;
  const currentUrl = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : '');
  
  return (
    <div className="min-h-screen w-full bg-[#F7F5FF]">
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content={author} />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        
        {/* Canonical URL */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Assembly" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:site" content="@assemblyhr" />
        <meta name="twitter:creator" content="@assemblyhr" />
        
        {/* Article Meta Tags */}
        {publishedTime && <meta property="article:published_time" content={publishedTime} />}
        {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
        <meta property="article:author" content={author} />
        <meta property="article:section" content="Employee Recognition" />
        <meta property="article:tag" content="employee recognition" />
        <meta property="article:tag" content="Assembly" />
        <meta property="article:tag" content="Bonusly" />
        <meta property="article:tag" content="comparison" />
        <meta property="article:tag" content="software review" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#7C3AED" />
        <meta name="msapplication-TileColor" content="#7C3AED" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Structured Data for Comparison */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": fullTitle,
            "description": description,
            "author": {
              "@type": "Organization",
              "name": author,
              "url": "https://assembly.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Assembly",
              "logo": {
                "@type": "ImageObject",
                "url": "https://assembly.com/assets/assembly-logo.png"
              }
            },
            "datePublished": publishedTime,
            "dateModified": modifiedTime,
            "image": ogImage,
            "url": currentUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "id": currentUrl
            },
            "about": [
              {
                "@type": "SoftwareApplication",
                "name": "Assembly",
                "applicationCategory": "Employee Recognition Software"
              },
              {
                "@type": "SoftwareApplication", 
                "name": "Bonusly",
                "applicationCategory": "Employee Recognition Software"
              }
            ]
          })}
        </script>
      </Helmet>
      <Navbar />
      <main className="w-full">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default CompanyLayout;
